import jsCookie from 'js-cookie';

// OneTrust's own cookies, read with js-cookie directly rather than through `utils/cookie`: that
// module coerces values to booleans and numbers and checks names against our own cookie registry,
// neither of which applies to cookies another party writes.
const OPTANON_CONSENT_COOKIE = 'OptanonConsent';
const OPTANON_ALERT_BOX_CLOSED_COOKIE = 'OptanonAlertBoxClosed';

/** The fields of `OptanonConsent` we read. OneTrust stores the cookie as a query string. */
interface OptanonConsent {
  /** How many times the user has answered the banner. */
  interactionCount?: string;
  /** When the CMP last wrote the cookie, which is not the same as when the user last decided. */
  datestamp?: string;
}

export function getOptanonConsent(): OptanonConsent {
  const value = jsCookie.get(OPTANON_CONSENT_COOKIE);

  return value ? (Object.fromEntries(new URLSearchParams(value)) as OptanonConsent) : {};
}

/**
 * Whether the user has ever answered the consent banner.
 *
 * This is what separates "declined" from "never asked": before any interaction the CMP still
 * publishes a full set of category decisions, and they are its defaults rather than the user's
 * choices.
 */
export function hasConsentInteraction(): boolean {
  const { interactionCount } = getOptanonConsent();

  return Number(interactionCount) > 0;
}

/**
 * When the user last made a privacy decision, as an ISO 8601 string.
 *
 * `OptanonAlertBoxClosed` is written only on a real interaction, which is exactly the event this
 * reports. `datestamp` is rewritten whenever the CMP touches its cookie — including page loads
 * where the user did nothing — so it stands in only once we know an interaction happened.
 *
 * @returns an empty string when the user has made no decision, or the stored date cannot be parsed
 */
export function getConsentLastUpdatedAt(): string {
  const { datestamp } = getOptanonConsent();
  const alertBoxClosed = jsCookie.get(OPTANON_ALERT_BOX_CLOSED_COOKIE);
  const rawDate = alertBoxClosed || (hasConsentInteraction() ? datestamp : undefined);

  if (!rawDate) {
    return '';
  }

  const date = new Date(rawDate);

  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
}
